import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import api, { fmt, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CircleDollarSign, HandCoins } from "lucide-react";
import { toast } from "sonner";

export default function Commissions() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const canPayout = isAdmin || user.can_payout;
  const [sales, setSales] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [filter, setFilter] = useState("all");
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/sales").then((r) => setSales(r.data));
    api.get("/payouts").then((r) => setPayouts(r.data));
    if (canPayout) api.get("/mechanics").then((r) => setMechanics(r.data));
  };
  useEffect(load, [canPayout]);

  const filtered = useMemo(() => {
    if (filter === "paid") return sales.filter((s) => s.commission_paid);
    if (filter === "unpaid") return sales.filter((s) => !s.commission_paid);
    return sales;
  }, [sales, filter]);

  const totalEarned = sales.reduce((s, x) => s + x.commission, 0);
  const unpaid = sales.filter((s) => !s.commission_paid).reduce((s, x) => s + x.commission, 0);

  const payout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payouts", { mechanic_id: confirm.id });
      toast.success(`Paid ${fmt(data.amount)} to ${data.mechanic_name}`);
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <div data-testid="commissions-page">
      <div className="mb-6">
        <div className="text-[10px] tracking-[0.4em] text-primary mb-2 flex items-center gap-3">
          <span className="h-px w-8 bg-primary/60" />
          COMMISSIONS <span className="font-jp text-muted-foreground">歩合給計算</span>
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight uppercase">
          {canPayout ? "Commission & Payouts" : "My Commission"}
        </h1>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-md p-5" data-testid="comm-total-earned">
          <span className="text-[10px] tracking-[0.25em] text-muted-foreground">TOTAL EARNED</span>
          <div className="font-display text-2xl font-black text-foreground mt-2">{fmt(totalEarned)}</div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="bg-card border border-amber-400/30 rounded-md p-5" data-testid="comm-unpaid">
          <span className="text-[10px] tracking-[0.25em] text-amber-400/80">UNPAID POOL</span>
          <div className="font-display text-2xl font-black text-amber-400 mt-2">{fmt(unpaid)}</div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 }} className="bg-card border border-primary/30 rounded-md p-5" data-testid="comm-paid">
          <span className="text-[10px] tracking-[0.25em] text-primary/80">PAID OUT</span>
          <div className="font-display text-2xl font-black text-primary mt-2">{fmt(totalEarned - unpaid)}</div>
        </motion.div>
      </div>

      {canPayout && (
        <div className="bg-card border border-border rounded-md p-5 mb-6" data-testid="payout-queue">
          <div className="flex items-center gap-2 mb-4">
            <HandCoins size={15} className="text-primary" />
            <span className="text-[10px] tracking-[0.25em] text-muted-foreground">CURRENT AMOUNTS OWED — ONE-CLICK PAYOUT</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {mechanics.map((m) => (
              <div key={m.id} className="border border-border rounded-sm p-4 flex items-center justify-between gap-3" data-testid={`owed-${m.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-foreground truncate">{m.name}</div>
                  <div className="text-[10px] text-muted-foreground">{m.rank} • {m.commission_rate}%</div>
                  <div className={`font-display text-lg font-black mt-1 ${m.unpaid_commission > 0 ? "text-amber-400" : "text-muted-foreground"}`}>
                    {fmt(m.unpaid_commission)}
                  </div>
                </div>
                <Button
                  data-testid={`payout-btn-${m.id}`}
                  disabled={m.unpaid_commission <= 0}
                  onClick={() => setConfirm(m)}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 font-display text-[10px] tracking-widest rounded-sm h-9 px-4"
                >
                  PAY OUT
                </Button>
              </div>
            ))}
            {!mechanics.length && <div className="text-muted-foreground text-xs py-4" data-testid="owed-empty">No mechanics on the roster yet.</div>}
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-md p-5 mb-6" data-testid="commission-ledger">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <span className="text-[10px] tracking-[0.25em] text-muted-foreground">SALES & COMMISSION LEDGER</span>
          <div className="flex gap-1.5" data-testid="ledger-filters">
            {["all", "unpaid", "paid"].map((f) => (
              <button
                key={f}
                data-testid={`filter-${f}`}
                onClick={() => setFilter(f)}
                className={`text-[10px] tracking-widest px-3 py-1.5 rounded-sm border transition-colors ${
                  filter === f
                    ? "bg-primary text-primary-foreground border-primary font-bold"
                    : "border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                {f.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="ledger-table">
            <thead>
              <tr className="text-left text-muted-foreground text-[10px] tracking-[0.2em] border-b border-border">
                <th className="pb-2 font-medium">DATE</th>
                <th className="pb-2 font-medium">CUSTOMER</th>
                {isAdmin && <th className="pb-2 font-medium">MECHANIC</th>}
                <th className="pb-2 font-medium text-right">SALE</th>
                <th className="pb-2 font-medium text-right">RATE</th>
                <th className="pb-2 font-medium text-right">COMMISSION</th>
                <th className="pb-2 font-medium text-right">STATUS</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} className="border-b border-border/50" data-testid={`ledger-row-${s.id}`}>
                  <td className="py-2.5 text-muted-foreground">{new Date(s.created_at).toLocaleDateString()}</td>
                  <td className="py-2.5 text-foreground">{s.customer}</td>
                  {isAdmin && <td className="py-2.5 text-muted-foreground">{s.mechanic_name}</td>}
                  <td className="py-2.5 text-right text-foreground">{fmt(s.total)}</td>
                  <td className="py-2.5 text-right text-muted-foreground">{s.commission_rate}%</td>
                  <td className="py-2.5 text-right text-primary font-semibold">{fmt(s.commission)}</td>
                  <td className="py-2.5 text-right">
                    <span className={`text-[10px] tracking-widest px-2 py-1 rounded-sm border ${s.commission_paid ? "border-emerald-400/40 text-emerald-400" : "border-amber-400/40 text-amber-400"}`}>
                      {s.commission_paid ? "PAID" : "OWED"}
                    </span>
                  </td>
                </tr>
              ))}
              {!filtered.length && (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-muted-foreground" data-testid="ledger-empty">
                    No records for this filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-card border border-border rounded-md p-5" data-testid="payout-history">
        <span className="text-[10px] tracking-[0.25em] text-muted-foreground">PAYOUT HISTORY</span>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-muted-foreground text-[10px] tracking-[0.2em] border-b border-border">
                <th className="pb-2 font-medium">DATE</th>
                <th className="pb-2 font-medium">MECHANIC</th>
                <th className="pb-2 font-medium">SALES SETTLED</th>
                <th className="pb-2 font-medium">PAID BY</th>
                <th className="pb-2 font-medium text-right">AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((p) => (
                <tr key={p.id} className="border-b border-border/50" data-testid={`history-row-${p.id}`}>
                  <td className="py-2.5 text-muted-foreground">{new Date(p.created_at).toLocaleString()}</td>
                  <td className="py-2.5 text-foreground">{p.mechanic_name}</td>
                  <td className="py-2.5 text-muted-foreground">{p.sales_count}</td>
                  <td className="py-2.5 text-muted-foreground">{p.paid_by}</td>
                  <td className="py-2.5 text-right text-primary font-semibold">{fmt(p.amount)}</td>
                </tr>
              ))}
              {!payouts.length && (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-muted-foreground" data-testid="history-empty">
                    No payouts recorded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!confirm} onOpenChange={() => setConfirm(null)}>
        <DialogContent className="bg-[#0C100D] border-primary/30 text-foreground max-w-sm" data-testid="payout-confirm-modal">
          {confirm && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2">
                  <CircleDollarSign size={16} className="text-primary" /> CONFIRM PAYOUT
                </DialogTitle>
              </DialogHeader>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Pay <span className="text-foreground font-semibold">{confirm.name}</span> their full unpaid
                commission of{" "}
                <span className="text-primary font-display font-bold">{fmt(confirm.unpaid_commission)}</span>?
                Their owed balance resets to $0 — the sales history stays.
              </p>
              <div className="flex gap-2 mt-2">
                <Button variant="outline" onClick={() => setConfirm(null)} data-testid="payout-cancel-btn" className="flex-1 border-border rounded-sm">
                  CANCEL
                </Button>
                <Button onClick={payout} disabled={busy} data-testid="payout-confirm-btn" className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-display text-[11px] tracking-widest">
                  {busy ? "PAYING…" : "CONFIRM"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
